import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import client from './graphql/client';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import DefaultLayout from './layout/DefaultLayout';
import DemoLayout from './layout/DemoLayout';
import SiteStudyLayout from './layout/SiteStudyLayout';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const DocumentDetailPage = lazy(() => import('./pages/DocumentDetailPage'));
const BankAccountsPage = lazy(() => import('./pages/BankAccountsPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const NewStudyPage = lazy(() => import('./pages/NewStudyPage'));
const StudiesPage = lazy(() => import('./pages/StudiesPage'));
const CreateActivityPage = lazy(() => import('./pages/CreateActivityPage'));
const CreateRemittancePage = lazy(() => import('./pages/CreateRemittancePage'));
const CreatePaymentPage = lazy(() => import('./pages/CreatePaymentPage'));
const CreateContractPage = lazy(() => import('./pages/CreateContractPage'));
const TransactionDetailPage = lazy(() => import('./pages/TransactionDetailPage'));
// Alcanza demo pages
const DemoOverviewPage = lazy(() => import('./pages/demo/OverviewPage'));
const DemoCtaPage = lazy(() => import('./pages/demo/CtaPage'));
const DemoGeneratePage = lazy(() => import('./pages/demo/GeneratePage'));
const DemoInvoiceListPage = lazy(() => import('./pages/demo/InvoiceListPage'));
const DemoInvoiceDetailPage = lazy(() => import('./pages/demo/InvoiceDetailPage'));

// Lazy-loaded site-study pages
const SummaryPage = lazy(() => import('./pages/site-study/SummaryPage'));
const ActivitiesPage = lazy(() => import('./pages/site-study/ActivitiesPage'));
const InvoicesPage = lazy(() => import('./pages/site-study/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('./pages/site-study/InvoiceDetailPage'));
const CreateInvoicePage = lazy(() => import('./pages/site-study/CreateInvoicePage'));
const PaymentsPage = lazy(() => import('./pages/site-study/PaymentsPage'));
const RemittanceDetailPage = lazy(() => import('./pages/site-study/RemittanceDetailPage'));
const SiteCostsPage = lazy(() => import('./pages/site-study/SiteCostsPage'));
const SiteStudyContractsPage = lazy(() => import('./pages/site-study/ContractsPage'));
const ContractDetailPage = lazy(() => import('./pages/site-study/ContractDetailPage'));
const SettingsPage = lazy(() => import('./pages/site-study/SettingsPage'));

// Protected layout route — guards all children, renders Outlet for nested routes
const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/signin" replace />;
  }

  return <Outlet />;
};

// Main App Routes Component
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Auth routes */}
      <Route
        path="/auth/signin"
        element={
          isAuthenticated ? (
            <Navigate to="/demo" replace />
          ) : (
            <Suspense fallback={<LoadingSpinner />}>
              <SignInPage />
            </Suspense>
          )
        }
      />
      {/* All protected routes share a single auth gate */}
      <Route element={<ProtectedRoute />}>
        {/* Alcanza demo — multi-page app with its own sidebar navigation */}
        <Route element={<DemoLayout />}>
          <Route path="/demo" element={<Navigate to="/demo/overview" replace />} />
          <Route path="/demo/overview" element={<DemoOverviewPage />} />
          <Route path="/demo/cta" element={<DemoCtaPage />} />
          <Route path="/demo/generate" element={<DemoGeneratePage />} />
          <Route path="/demo/invoices" element={<DemoInvoiceListPage />} />
          <Route path="/demo/invoices/:invoiceId" element={<DemoInvoiceDetailPage />} />
        </Route>
        {/* Global scope routes — DefaultLayout with tab navigation */}
        <Route element={<DefaultLayout />}>
          <Route path="/summary" element={<DashboardPage />} />
          <Route path="/dashboard" element={<Navigate to="/demo" replace />} />
          <Route path="/studies" element={<StudiesPage />} />
          <Route path="/studies/new" element={<NewStudyPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/:documentId" element={<DocumentDetailPage />} />
          <Route path="/bank-accounts" element={<BankAccountsPage />} />
          <Route path="/transactions/:transactionId" element={<TransactionDetailPage />} />
        </Route>

        {/* SSI-scoped routes (SiteStudyLayout with nested Outlet) */}
        <Route
          path="/ssi/:ssiId"
          element={<SiteStudyLayout />}
        >
          <Route index element={<SummaryPage />} />
          <Route path="activities" element={<ActivitiesPage />} />
          <Route path="activities/new" element={<CreateActivityPage />} />
          <Route path="site-costs" element={<SiteCostsPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/new" element={<CreateInvoicePage />} />
          <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="payments/new" element={<CreatePaymentPage />} />
          <Route path="payments/:transactionId" element={<TransactionDetailPage />} />
          {/* Legacy routes -- redirect to /payments */}
          <Route path="remittances" element={<Navigate to="../payments" replace />} />
          <Route path="transactions" element={<Navigate to="../payments" replace />} />
          {/* Keep remittance detail/create routes for deep links */}
          <Route path="remittances/new" element={<CreateRemittancePage />} />
          <Route path="remittances/:remittanceId" element={<RemittanceDetailPage />} />
          <Route path="contracts" element={<SiteStudyContractsPage />} />
          <Route path="contracts/new" element={<CreateContractPage />} />
          <Route path="contracts/:contractId" element={<ContractDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="notes" element={<Navigate to="../settings" replace />} />
        </Route>
      </Route>

      {/* Default redirects */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/demo" replace />
          ) : (
            <Navigate to="/auth/signin" replace />
          )
        }
      />
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate to="/demo" replace />
          ) : (
            <Navigate to="/auth/signin" replace />
          )
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <ErrorBoundary>
          <div className="App">
            <AppRoutes />

            <ToastContainer
              position="bottom-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </div>
        </ErrorBoundary>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default App;
